import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { getEmployeeByIdAction, getEmployeeRoleAction, getStateCityAction, loadingToggleAction, updateEmployeeAction } from "../../../store/actions/employee/EmployeeActions";
import ShimmerLoader from "../../pages/ShimmerLoader/ShimmerLoader";
import { toast } from "react-toastify";

// Validation schema
const editEmployeeSchema = Yup.object().shape({
  email: Yup.string()
    .matches(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, "Enter a valid Email")
    .required("Please enter an email"),
  name: Yup.string()
    .required("Please enter valid name")
    .min(1, "Minimum 1 characters")
    .max(65, "Maximum 65 characters")
    .matches(/^(?! )[A-Za-z0-9. ]+(?<! )$/, "Only letters, spaces, and dots are allowed & space not allowed at start or end & no special characters are allowed"),
  display_name: Yup.string()
    .required("Please enter valid display name")
    .min(1, "Minimum 1 characters")
    .max(20, "Maximum 20 characters")
    .matches(/^(?! )[A-Za-z0-9. ]+(?<! )$/, "Only letters, spaces, and dots are allowed & space not allowed at start or end & no special characters are allowed"),
  phone: Yup.string()
    .required("Enter a phone number")
    .matches(/^[6789][0-9]{9}$/, "Please enter valid phone number"),
  pincode: Yup.string()
    .required("Please enter  valid pincode")
    .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
  city: Yup.string().required("Please enter valid city"),
  state: Yup.string().required("Please enter valid state"),
  address: Yup.string()
    .required("Please enter valid address")
    .matches(/^(?![ ,/])(?=.*[^\s#@])[\w, /-]*[^\s,/-]$/, "Address must not start or end with a space, comma, or slash, and cannot contain @ or #."),
  role_id: Yup.string().required("Please select one role"),
  dob: Yup.date().required("Please delect date of birth").nullable().max(new Date(), "Date of birth cannot be in the future"),
});

const EditEmployee = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [roleOptions, setRoleOptions] = useState([]);
  const [initialValues, setInitialValues] = useState({
    email: "",
    name: "",
    display_name: "",
    phone: "",
    pincode: "",
    city: "",
    state: "",
    address: "",
    role_id: "",
    dob: null,
  });

  const { roles, loading, error, employeeById } = useSelector((state) => state.employee);

  useEffect(() => {
    if (!id) {
      navigate("/users");
      return;
    }
    dispatch(getEmployeeByIdAction(id, navigate));
    dispatch(getEmployeeRoleAction(navigate));
  }, [dispatch, id, navigate]);

  useEffect(() => {
    if (employeeById && employeeById.length > 0) {
      const employee = employeeById[0];
      setInitialValues({
        email: employee.email || "",
        name: employee.name || "",
        display_name: employee.display_name || "",
        phone: employee.phone || "",
        pincode: employee.pincode || "",
        city: employee.city || "",
        state: employee.state || "",
        address: employee.address || "",
        role_id: employee.role_id || "",
        dob: employee.dob ? new Date(employee.dob) : null,
      });
    }
  }, [employeeById]);

  useEffect(() => {
    if (roles && roles.length > 0) {
      const options = roles.map((role) => ({
        value: role.role_id,
        label: role.role_name,
      }));
      setRoleOptions(options);
    }
  }, [roles]);

  const handleSubmit = (values, { setSubmitting }) => {
    if (values.dob) {
      values.dob = format(values.dob, "yyyy-MM-dd");
    }
    dispatch(updateEmployeeAction(values, id, navigate));
    // setSubmitting(false);
  };

  const cancelHandler = () => {
    navigate("/users");
  };

  const handleKeyPress = (event) => {
    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
    if (!/[0-9]/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  // Toast default options
  const defaultOptions = {
    position: toast.POSITION.TOP_RIGHT,
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    rtl: false,
    pauseOnFocusLoss: true,
    draggable: true,
    pauseOnHover: true,
    theme: "colored",
  };

  const checkPincode = async (pincode, setFieldValue) => {
    if (pincode.length === 6) {
      const payload = { pincode };
      try {
        const response = await getStateCityAction(payload);
        if (response.status === 404) {
          toast.info("Please enter valid pincode", defaultOptions);
          return;
        }
        if (response.status === 200) {
          const {
            data: {
              result: {
                data: { city: city, state: state },
              },
            },
          } = response;
          setFieldValue("city", city);
          setFieldValue("state", state);
          return;
        }
      } catch (error) {
        toast.error("something went wrong", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          rtl: false,
          pauseOnFocusLoss: true,
          draggable: true,
          pauseOnHover: true,
          theme: "colored",
        });
      }
    } else {
      setFieldValue("state", "");
      setFieldValue("city", "");
    }
  };

  return (
    <>
      <div className='page-titles'>
        <ol className='breadcrumb'>
          <li className='breadcrumb-item'>
            <Link to={"/users"}>User</Link>
          </li>
          <li className='breadcrumb-item active'>
            <Link to={"#"}>Edit User</Link>
          </li>
        </ol>
      </div>
      {loading && <ShimmerLoader width='95%' height='20%' count={4} />}
      {!loading && (
        <div className='row'>
          <div className='col-lg-12'>
            <div className='card'>
              <div className='card-body'>
                <div className='form-validation'>
                  <Formik initialValues={initialValues} validationSchema={editEmployeeSchema} onSubmit={handleSubmit} enableReinitialize>
                    {({ setFieldValue, values, isSubmitting }) => (
                      <Form>
                        <div className='mb-3 row'>
                          <div className='col-6'>
                            <label className='col-form-label'>
                              Name
                              <span className='text-danger'>*</span>
                            </label>
                            <div className=''>
                              <Field type='text' name='name' className='form-control' placeholder='Name' maxLength={65} />
                              <ErrorMessage name='name' component='div' className='text-danger' />
                            </div>
                          </div>
                          <div className='col-6'>
                            <label className='col col-form-label'>
                              Email
                              <span className='text-danger'>*</span>
                            </label>
                            <div className='col'>
                              <Field type='text' disabled name='email' className='form-control' placeholder='Email' maxLength={65} />
                              <ErrorMessage name='email' component='div' className='text-danger' />
                            </div>
                          </div>
                        </div>
                        <div className='mb-3 row'>
                          <div className='col-6'>
                            <label className='col-form-label'>
                              Display Name
                              <span className='text-danger'>*</span>
                            </label>
                            <div className=''>
                              <Field type='text' name='display_name' className='form-control' placeholder='Display Name' maxLength={20} />
                              <ErrorMessage name='display_name' component='div' className='text-danger' />
                            </div>
                          </div>
                          <div className='col-6'>
                            <label className='col col-form-label'>
                              Phone
                              <span className='text-danger'>*</span>
                            </label>
                            <div className='col'>
                              <Field type='text' name='phone' maxLength={10} className='form-control' placeholder='Phone Number' onKeyPress={handleKeyPress} />
                              <ErrorMessage name='phone' component='div' className='text-danger' />
                            </div>
                          </div>
                        </div>
                        <div className='mb-3 row'>
                          <div className='col-6'>
                            <label className='col-form-label'>
                              Role
                              <span className='text-danger'>*</span>
                            </label>
                            <div className='col'>
                              <select name='role_id' className='form-control' id='inputRole' value={values.role_id || ""} onChange={(e) => setFieldValue("role_id", e.target.value)}>
                                <option value=''>Choose Role</option>
                                {roleOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <ErrorMessage name='role_id' component='div' className='text-danger' />
                            </div>
                          </div>
                          <div className='col-6'>
                            <label className='col-form-label'>
                              Date of Birth
                              <span className='text-danger'>*</span>
                            </label>
                            <div className='w-100'>
                              <DatePicker showMonthDropdown showYearDropdown peekNextMonth dropdownMode='select' selected={values.dob ? new Date(values.dob) : null} onChange={(date) => setFieldValue("dob", date)} className='form-control' maxDate={new Date()} placeholderText='Select Date' dateFormat='yyyy-MM-dd' onKeyDown={(e) => e.preventDefault()} />
                              <ErrorMessage name='dob' component='div' className='text-danger' />
                            </div>
                          </div>
                          <div className='col-6'>
                            <label className='col col-form-label'>
                              Pincode
                              <span className='text-danger'>*</span>
                            </label>
                            <div className='col'>
                              <Field
                                type='text'
                                maxLength={6}
                                name='pincode'
                                className='form-control'
                                placeholder='Enter Pincode'
                                onChange={(e) => {
                                  setFieldValue("pincode", e.target.value);
                                  checkPincode(e.target.value, setFieldValue);
                                }}
                                onKeyPress={(e) => {
                                  const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
                                  if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                              <ErrorMessage name='pincode' component='div' className='text-danger' />
                            </div>
                          </div>

                          <div className='col-6'>
                            <label className='col-form-label'>
                              City
                              <span className='text-danger'>*</span>
                            </label>
                            <div className=''>
                              <Field type='text' disabled name='city' className='form-control' placeholder='' maxLength={65} />
                              <ErrorMessage name='city' component='div' className='text-danger' />
                            </div>
                          </div>
                        </div>

                        <div className='mb-3 row'>
                          <div className='col-lg-6 col-sm-12'>
                            <label className='col-form-label'>
                              State<span className='text-danger'>*</span>
                            </label>
                            <Field disabled type='text' name='state' className='form-control' placeholder='' maxLength={65} />
                            <ErrorMessage name='state' component='div' className='text-danger' />
                          </div>
                          {/* <div className='col-6'>
                            <label className='col col-form-label'>
                              State
                              <span className='text-danger'>*</span>
                            </label>
                            <div className='col'>
                              <select name='state' className='form-control' id='inputState' value={values.state} onChange={(e) => setFieldValue("state", e.target.value)}>
                                <option value=''>Select State</option>
                                <option value='AP'>Andhra Pradesh</option>
                                <option value='AR'>Arunachal Pradesh</option>
                                <option value='AS'>Assam</option>
                                <option value='BR'>Bihar</option>
                                <option value='CG'>Chhattisgarh</option>
                                <option value='GA'>Goa</option>
                                <option value='GJ'>Gujarat</option>
                                <option value='HR'>Haryana</option>
                                <option value='HP'>Himachal Pradesh</option>
                                <option value='JH'>Jharkhand</option>
                                <option value='KA'>Karnataka</option>
                                <option value='KL'>Kerala</option>
                                <option value='MP'>Madhya Pradesh</option>
                                <option value='MH'>Maharashtra</option>
                                <option value='MN'>Manipur</option>
                                <option value='ML'>Meghalaya</option>
                                <option value='MZ'>Mizoram</option>
                                <option value='NL'>Nagaland</option>
                                <option value='OR'>Odisha</option>
                                <option value='PB'>Punjab</option>
                                <option value='RJ'>Rajasthan</option>
                                <option value='SK'>Sikkim</option>
                                <option value='TN'>Tamil Nadu</option>
                                <option value='TS'>Telangana</option>
                                <option value='TR'>Tripura</option>
                                <option value='UP'>Uttar Pradesh</option>
                                <option value='UK'>Uttarakhand</option>
                                <option value='WB'>West Bengal</option>
                                <option value='AN'>Andaman and Nicobar Islands</option>
                                <option value='CH'>Chandigarh</option>
                                <option value='DN'>Dadra and Nagar Haveli and Daman and Diu</option>
                                <option value='LD'>Lakshadweep</option>
                                <option value='DL'>Delhi</option>
                                <option value='PY'>Puducherry</option>
                                <option value='LA'>Ladakh</option>
                                <option value='JK'>Jammu and Kashmir</option>
                              </select>
                              <ErrorMessage name='state' component='div' className='text-danger' />
                            </div>
                          </div> */}
                          <div className='col-6'>
                            <label className='col-form-label'>
                              Address
                              <span className='text-danger'>*</span>
                            </label>
                            <div className=''>
                              <Field type='text' name='address' className='form-control' placeholder='Address' maxLength={65} />
                              <ErrorMessage name='address' component='div' className='text-danger' />
                            </div>
                          </div>
                        </div>
                        <div className='mb-3 row text-center'>
                          <div className='col-lg-9 mx-auto'>
                            <button type='button' className='btn btn-danger me-3' onClick={cancelHandler}>
                              Cancel
                            </button>
                            <button type='submit' className='btn btn-primary' disabled={isSubmitting}>
                              {isSubmitting ? "Saving" : "Submit"}
                            </button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditEmployee;
